import { Link } from "react-router";
import { Card, CardContent, CardHeader, Tag } from "@diametral/design-system/react";

import type { ActivityReport } from "../api/types";
import { formatDateTime } from "../lib/format";
import { useTranslation } from "../lib/i18n";
import { CHANNEL } from "../lib/labels";
import { REPORT_FIELD_DEFAULTS } from "../lib/reference";
import { compareWeeks, isIsoWeek, weekLabel } from "../lib/week";
import { ReportStatusTag } from "./Badges";
import { PersonCell } from "./PersonCell";
import { PlainText } from "./PlainText";

/** Reports grouped by week, most recent week first (order inside a week is kept). */
export function groupByWeek(reports: ActivityReport[]): Array<{ week: string; reports: ActivityReport[] }> {
  const groups = new Map<string, ActivityReport[]>();
  for (const report of reports) {
    const list = groups.get(report.week) ?? [];
    list.push(report);
    groups.set(report.week, list);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => compareWeeks(b, a))
    .map(([week, items]) => ({ week, reports: items }));
}

export function ReportCard({ report, showProject = false }: { report: ActivityReport; showProject?: boolean }) {
  const { t } = useTranslation();
  return (
    <Card size="sm">
      <CardHeader>
        <div className="ada-row">
          <span className="ada-cluster">
            <PersonCell person={report.person} />
            {showProject ? (
              <Link className="ada-link" to={`/projects/${report.project}`}>
                {report.project_title}
              </Link>
            ) : null}
          </span>
          <span className="ada-cluster">
            {report.absent ? <Tag tone="info">{t("Absent / no activity")}</Tag> : null}
            {report.status === "draft" ? <ReportStatusTag status="draft" /> : null}
            <span className="ada-faint" title={report.submitted_at ? t("Submitted on {{date}}", { date: formatDateTime(report.submitted_at) }) : undefined}>
              {report.source !== "ui" ? `${CHANNEL[report.source]} · ` : ""}
              {formatDateTime(report.submitted_at ?? report.updated_at)}
            </span>
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <dl className="ada-report-fields">
          <div>
            <dt className="ds-label">{REPORT_FIELD_DEFAULTS.done.label}</dt>
            <dd>
              <PlainText text={report.done} empty={report.absent ? "—" : t("Not filled in")} />
            </dd>
          </div>
          <div>
            <dt className="ds-label">{REPORT_FIELD_DEFAULTS.next.label}</dt>
            <dd>
              <PlainText text={report.next} />
            </dd>
          </div>
          <div>
            <dt className="ds-label">{REPORT_FIELD_DEFAULTS.blockers.label}</dt>
            <dd>
              <PlainText text={report.blockers} />
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}

export function ReportsByWeek({ reports, showProject = false }: { reports: ActivityReport[]; showProject?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="ada-stack ada-stack--loose">
      {groupByWeek(reports).map(({ week, reports: items }) => (
        <section key={week} className="ada-week" aria-label={isIsoWeek(week) ? weekLabel(week) : week}>
          <div className="ada-week__head">
            <h3 className="ada-section-title">{isIsoWeek(week) ? weekLabel(week) : week}</h3>
            <span className="ada-faint">{t("{{count}} reports", { count: items.length })}</span>
          </div>
          {items.map((report) => (
            <ReportCard key={report.id} report={report} showProject={showProject} />
          ))}
        </section>
      ))}
    </div>
  );
}
